import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Fade,
  Grow,
  ClickAwayListener,
  Tooltip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { Rating } from "@mui/material";
import AppLoader from "../components/AppLoader";

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import BoltIcon from "@mui/icons-material/Bolt";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ChatIcon from "@mui/icons-material/Chat";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import MicIcon from "@mui/icons-material/Mic";
import ExploreIcon from "@mui/icons-material/Explore";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from "@mui/icons-material/Replay";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RepeatIcon from "@mui/icons-material/Repeat";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import StormIcon from "@mui/icons-material/Storm";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import LanguageIcon from "@mui/icons-material/Language";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";

import { getPitchTypeById } from "../services/pitchTypesService";
import { getAIProfileById } from "../services/aiProfilesService";
// BarChart Insights
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignIcon from "@mui/icons-material/Campaign";
// import MicIcon from "@mui/icons-material/Mic";
import { ReactComponent as SharkIcon } from "../assets/icons/shark-2.svg";
import CoachSuggestionText from "../components/CoachSuggestionText";

import { useAuth } from "../hooks/AuthContext";

import CircularAccessBar from "../components/partners/CircularAccessBar";
import AppBackground from "../components/layout/AppBackground";


const muiIconMap = {
  Star: StarIcon,
  EmojiEvents: EmojiEventsIcon,
  Whatshot: WhatshotIcon,
  Bolt: BoltIcon,
  FlashOn: FlashOnIcon,
  Chat: ChatIcon,
  RecordVoiceOver: RecordVoiceOverIcon,
  Mic: MicIcon,
  Explore: ExploreIcon,
  TravelExplore: TravelExploreIcon,
  Repeat: RepeatIcon,
  CenterFocusStrong: CenterFocusStrongIcon,
  Storm: StormIcon,
  LabelImportant: LabelImportantIcon,
  ConnectWithoutContact: ConnectWithoutContactIcon,
  Language: LanguageIcon,
  TrackChanges: TrackChangesIcon,
  ShowChart: ShowChartIcon,
  GraphicEq: GraphicEqIcon
};

const Dashboard = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const [openLEXTooltip, setOpenLEXTooltip] = useState(false);
  const [openWPMTooltip, setOpenWPMTooltip] = useState(false);

  const handlePracticeAgain = async () => {
    try {
      const pitchType = await getPitchTypeById(last_session.session.pitch_type_id);
      const aiProfile = await getAIProfileById(last_session.session.ai_profile_id);
      navigate("/pitch-start", {
        state: {
          pitchType,
          aiProfile,
        },
      });
    } catch (err) {
      console.error("Error loading pitchType or aiProfile:", err);
    }
  };

  const handleViewSession = () => {
    navigate(`/evaluation/${last_session.session.id}`);
  };

  const [lastSessionAccordionOpen, setLastSessionAccordionOpen] = useState(() => {
    const stored = localStorage.getItem("lastSessionAccordionOpen");
    return stored ? JSON.parse(stored) : true;
  });
  const handleLastSessionAccordionToggle = (_event, isExpanded) => {
    setLastSessionAccordionOpen(isExpanded);
    localStorage.setItem("lastSessionAccordionOpen", JSON.stringify(isExpanded));
  };

  const [metricsAccordionOpen, setmetricsAccordionOpen] = useState(() => {
    const stored = localStorage.getItem("metricsAccordionOpen");
    return stored ? JSON.parse(stored) : true;
  });
  const handlemetricsAccordionToggle = (_event, isExpanded) => {
    setmetricsAccordionOpen(isExpanded);
    localStorage.setItem("metricsAccordionOpen", JSON.stringify(isExpanded));
  };

  const [coachAccordionOpen, setCoachAccordionOpen] = useState(() => {
    const stored = localStorage.getItem("coachAccordionOpen");
    return stored ? JSON.parse(stored) : true; // abierto por defecto
  });
  const handleCoachAccordionToggle = () => {
    const newValue = !coachAccordionOpen;
    setCoachAccordionOpen(newValue);
    localStorage.setItem("coachAccordionOpen", JSON.stringify(newValue));
  };

  const getLexicalColor = (value) => {
    if (value >= 0.6) return "success";     // verde
    if (value >= 0.3) return "warning";     // amarillo
    return "error";                         // rojo
  };

  const getWPMColor = (wpm) => {
    if (wpm < 90) return "error.main";
    if (wpm <= 130) return "success.main";
    if (wpm <= 160) return "warning.main";
    return "error.dark";
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get("/api/user/dashboard");
        setData(res.data);
      } catch (err) {
        console.error("Error loading dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading || !data) {
    return <AppLoader message={t("loading")} />;
  }

  if (data?.message === "No data available") {
    return (
      <>
        {user?.is_partner_user && user?.source === "circular" && (
          <CircularAccessBar />
        )}

        <AppBackground
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            pt: { xs: 3, md: 4 },
          }}
        >
          <Box sx={{ width: "100%" }}>
            {renderUndeltaHackathonCard()}
            <Box
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                borderRadius: 4,
                p: 4,
                maxWidth: 500,
                textAlign: "center",
                color: "#fff",
                mx: "auto",
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t("no_data_title")}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {t("no_data_message")}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/pitch-start")}
                sx={{ mt: 2, color: "#fff", borderColor: "#fff" }}
              >
                {t("new_session")}
              </Button>
            </Box>
          </Box>
        </AppBackground>
      </>
    );
  }

  const {
    global_score,
    metrics,
    recent_progress,
    word_stats,
    session_stats,
    achievements,
    speech_stats,
    coach_suggestion,
    last_session,
  } = data

  const metricList = [
    { key: "objective", label: t("metrics.objective"), value: metrics.objective, progress: recent_progress?.objective_achievement },
    { key: "clarity", label: t("metrics.clarity"), value: metrics.clarity, progress: recent_progress?.clarity },
    { key: "relevance", label: t("metrics.relevance"), value: metrics.relevance, progress: recent_progress?.relevance },
    { key: "fluency", label: t("metrics.fluency"), value: metrics.fluency, progress: recent_progress?.fluency },
    { key: "engagement", label: t("metrics.engagement"), value: metrics.engagement, progress: recent_progress?.engagement },
  ];

  const iconColorMap = {
    info: "#2196f3",
    error: "#f44336",
    success: "#4caf50",
    warning: "#ff9800",
    default: "#aaa",
  };

  const scoreColor = (value) => {
    if (value >= 4.5) return "#10B981"; // Experto - verde
    if (value >= 3.5) return "#A855F7"; // Avanzado - violeta
    if (value >= 2.0) return "#3B82F6"; // Intermedio - azul
    return "#9CA3AF";                   // Principiante - gris
  };

  const levelLabel = (value) => {
    if (value >= 4.5) return t("level.expert");
    if (value >= 3.5) return t("level.advanced");
    if (value >= 2.0) return t("level.intermediate");
    return t("level.beginner");
  };

  const formatNumber = (value, i18n) => {
    const localeMap = {
      es: "es-ES",
      en: "en-US",
    };
    const locale = localeMap[i18n.language] || i18n.language;
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      useGrouping: true, // activa separadores de miles
    }).format(value);
  };

  const getIconForPitchType = (name) => {
    const lower = name.toLowerCase();

    if (lower.includes("elevator")) return <TrendingUpIcon />;
    if (lower.includes("demo")) return <CampaignIcon />;
    if (lower.includes("shark")) return <SharkIcon style={{ width: 22, height: 22 }} />;
    if (lower.includes("media") || lower.includes("medio")) return <MicIcon />;

    return <RocketLaunchIcon />;
  };

  function renderUndeltaHackathonCard() {
    if (!(user?.is_partner_user && user?.source === "undelta")) return null;

    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(44, 24, 74, 0.96) 0%, rgba(63, 28, 105, 0.92) 55%, rgba(30, 17, 57, 0.96) 100%)",
          borderRadius: 4,
          maxWidth: 800,
          mx: "auto",
          mb: 3,
          px: { xs: 2, md: 3 },
          py: 2.5,
          border: "1px solid rgba(168, 85, 247, 0.35)",
          boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
          position: "relative",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            src="/images/ai_profiles/tincho.jpg"
            alt="Tincho"
            sx={{
              width: { xs: 42, sm: 50 },
              height: { xs: 42, sm: 50 },
              border: "3px solid rgba(250, 204, 21, 0.95)",
              boxShadow: "0 12px 24px rgba(0,0,0,0.28)",
              mt: 0,
              flexShrink: 0,
            }}
          />

          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 0.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: 800,
                  letterSpacing: 0.2,
                }}
              >
                {t("undelta_pass_title")}
              </Typography>
              <Chip
                label={t("undelta_pass_badge")}
                size="small"
                sx={{
                  backgroundColor: "rgba(250, 204, 21, 0.18)",
                  color: "#FDE68A",
                  border: "1px solid rgba(250, 204, 21, 0.42)",
                  fontWeight: 700,
                  letterSpacing: 0.35,
                }}
              />
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "#E9D5FF",
                fontWeight: 600,
                mb: 0,
              }}
            >
              {t("undelta_pass_subtitle")}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

    return (
    <>
      {user?.is_partner_user && user?.source === "circular" && (
        <CircularAccessBar />
      )}

      <AppBackground
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          pt: { xs: 3, md: 4 },
        }}
      >
        <Box sx={{ width: "100%" }}>
          {renderUndeltaHackathonCard()}
          <Fade in timeout={700}>
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.75)",
                borderRadius: 4,
                maxWidth: 800,
                mx: "auto",
                px: { xs: 2, md: 3 },
                py: 4,
              }}
            >
              <Typography variant="h5" mb={4} fontWeight="bold" sx={{textAlign: { xs: "center", sm: "left" }}}>
                {t("title")}
              </Typography>

            {/* Score & Nivel */}
            <Grow in timeout={800}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 4,
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: 2,
                  py: 3,
                  mb: 3,
                }}
              >
                {/* Global Score con progreso circular */}
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={(global_score / 5) * 100}
                    size={80}
                    thickness={5}
                    sx={{
                      color: scoreColor(global_score),
                      backgroundColor: "rgba(255,255,255,0.05)",
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#fff", fontWeight: "bold", lineHeight: 1 }}
                    >
                      {global_score.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
                {/* Nivel actual */}
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography variant="body2">{t("level.title")}</Typography>
                  <Typography variant="h6">{levelLabel(global_score)}</Typography>
                </Box>
              </Box>
            </Grow>

            {/* Sesiones, palabras, WPM, Div.Lex. */}
            <Grow in timeout={900}>
              <Card
                sx={{
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  {/* Sesiones */}
                  <Box sx={{ flex: { xs: "1 1 50%", md: "1 1 25%" }, mb: { xs: 2, md: 0 } }}>
                    <Typography variant="body2">{t("sessions_completed")}</Typography>
                    <Typography variant="h6">
                      {formatNumber(session_stats.total_sessions, i18n)}
                    </Typography>
                  </Box>

                  {/* Palabras */}
                  <Box sx={{ flex: { xs: "1 1 50%", md: "1 1 25%" }, mb: { xs: 2, md: 0 } }}>
                    <Typography variant="body2">{t("word_count")}</Typography>
                    <Typography variant="h6">
                      {formatNumber(word_stats.total_words, i18n)}
                    </Typography>
                  </Box>

                  {/* WPM */}
                  <Box
                    sx={{
                      flex: { xs: "1 1 50%", md: "1 1 25%" },
                      mb: { xs: 2, md: 0 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2">{t("wpm_user")}</Typography>
                      <ClickAwayListener onClickAway={() => setOpenWPMTooltip(false)}>
                        <Box display="inline">
                          <Tooltip
                            title={
                              <Typography variant="caption">
                                {t("wpm_user_explained_1")}<br />
                                {t("wpm_user_explained_2")}<br />
                                {t("wpm_user_explained_3")}
                              </Typography>
                            }
                            placement="top"
                            arrow
                            open={openWPMTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                          >
                            <IconButton size="small" onClick={() => setOpenWPMTooltip((prev) => !prev)}>
                              <InfoOutlinedIcon sx={{ color: "#ccc" }} fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ClickAwayListener>
                    </Box>
                    <Box mt={0.5}>
                      <Box
                        sx={{
                          height: 18,
                          minWidth: 10,
                          width: "fit-content",
                          backgroundColor: getWPMColor(speech_stats?.avg_wpm),
                          borderRadius: 2,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          color: "#fff",
                          px: 1.5,
                        }}
                      >
                        {speech_stats?.avg_wpm?.toFixed(0)} WPM
                      </Box>
                    </Box>
                  </Box>

                  {/* Diversidad léxica */}
                  <Box
                    sx={{
                      flex: { xs: "1 1 50%", md: "1 1 25%" },
                      mb: { xs: 2, md: 0 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={0.5}
                    >
                      <Typography variant="body2">{t("lexical_diversity")}</Typography>
                      <ClickAwayListener onClickAway={() => setOpenLEXTooltip(false)}>
                        <Box display="inline">
                          <Tooltip
                            title={
                              <Typography variant="caption">
                                {t("lexical_diversity_explained_1")}<br />
                                {t("lexical_diversity_explained_2")}<br />
                                {t("lexical_diversity_explained_3")}<br />
                                {t("lexical_diversity_explained_4")}
                              </Typography>
                            }
                            placement="top"
                            arrow
                            open={openLEXTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                          >
                            <IconButton
                              size="small"
                              onClick={() => setOpenLEXTooltip((prev) => !prev)}
                            >
                              <InfoOutlinedIcon sx={{ color: "#ccc" }} fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ClickAwayListener>
                    </Box>

                    {/* Barra de progreso */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: 120,
                        mx: "auto",
                        mt: 0.5,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={speech_stats?.avg_lexical_diversity * 100}
                        color={getLexicalColor(speech_stats?.avg_lexical_diversity)}
                        sx={{
                          height: 18,
                          borderRadius: 2,
                          backgroundColor: "#333",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        {(speech_stats?.avg_lexical_diversity * 100).toFixed(0)}%
                      </Box>
                    </Box>
                  </Box>


                </Box>
              </Card>
            </Grow>

            {/* COACH_SUGGESTION  */}
            {coach_suggestion.suggested_metric && (
              <Grow in timeout={1000}>
                <Box mt={2} backgroundColor="rgba(255, 255, 255, 0.05)" pt={1} pb={0} px={1} borderRadius={2}>
                  <Accordion
                    // defaultExpanded
                    expanded={coachAccordionOpen}
                    onChange={handleCoachAccordionToggle}
                    disableGutters
                    elevation={0}
                    sx={{
                      backgroundColor: "transparent",
                      borderRadius: 2,
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon sx={{ color: "#fff", mt: "0px" }} />
                      }
                      sx={{
                        px: 0,
                        pb: 0,
                        mb: 0,
                        color: "#fff",
                        alignItems: "flex-start",
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                          margin: 0,
                          "&.Mui-expanded": {
                            margin: 0,
                          },
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#a855f7", mb: 0, py: 0 }}
                      >
                        <PsychologyAltOutlinedIcon fontSize="small" sx={{ color: "#a855f7" }} />
                        {t("coach_suggestion_title")}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 1, pt: 0, mt: 0 }}>
                      <CoachSuggestionText suggestedMetric={coach_suggestion.suggested_metric} />

                      <Box mt={0} mb={1} display="flex" justifyContent="center">
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ borderRadius: '20px', mt: 2, textTransform: 'none' }}
                          onClick={handlePracticeAgain}
                        >
                          {t("coach_suggestion_button")}
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grow>
            )}

            {/* Info Ultima sesion */}
            {typeof session_stats.last_session_days_ago === "number" && last_session && (
              <Grow in timeout={1100}>
                <Card
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    mt: 3,
                  }}
                >
                  <Accordion
                    defaultExpanded
                    expanded={lastSessionAccordionOpen}
                    onChange={handleLastSessionAccordionToggle}
                    disableGutters
                    elevation={0}
                    sx={{
                      backgroundColor: "transparent",
                      borderRadius: 2,
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon sx={{ color: "#fff", mt: "0px" }} />
                      }
                      sx={{
                        px: 0,
                        pb: 0,
                        mt: 1,
                        mb: 1,
                        color: "#fff",
                        alignItems: "flex-start",
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                          margin: 0,
                          "&.Mui-expanded": {
                            margin: 0,
                          },
                        },
                      }}
                    >
                      {/* Primera fila: fecha */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 33%" }, mb: 2 }}>
                          <Typography variant="body2">{t("last_session")}</Typography>
                          <Typography variant="h6">
                            {formatNumber(session_stats.last_session_days_ago, i18n)} {t("last_session_days_ago")}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Segunda fila: Pitch Type y AI Profile */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: {
                            xs: "space-between", // mobile: extremos
                            md: "center",         // desktop: centrado
                          },
                          alignItems: "center",
                          gap: { xs: 0, md: 10 },
                          mb: 1,
                        }}
                      >
                        {/* Pitch Type */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              bgcolor: "#a855f7",
                              color: "#fff",
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              mt: "2px",
                            }}
                          >
                            {getIconForPitchType(i18n.language?.startsWith("es")
                              ? last_session?.session?.pitch_type_name_es
                              : last_session?.session?.pitch_type_name_en)}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              lineHeight: 1.2,
                            }}
                          >
                            {i18n.language?.startsWith("es")
                              ? last_session?.session?.pitch_type_name_es
                              : last_session?.session?.pitch_type_name_en}
                          </Typography>
                        </Box>

                        {/* AI Profile */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                          <Avatar
                            src={last_session?.session?.ai_profile_image}
                            alt="AI Avatar"
                            sx={{ width: 28, height: 28 }}
                          />
                          <Typography variant="body2" noWrap>
                            {i18n.language?.startsWith("es")
                              ? last_session?.session?.ai_profile_name_es
                              : last_session?.session?.ai_profile_name_en}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 0, pt: 0, mt: 0 , mb: 0 }}>
                      {/* Tercera fila: Comentario de la AI */}
                      <Box sx={{ mt: 1, mb: 0 }}>
                        {last_session.aiprofile_comments && (
                          <>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {t('aiprofile_comments_title', {aiprofile: last_session?.session?.ai_profile_name_es || last_session?.session?.ai_profile_name_en})}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: "italic",
                                color: "#ccc",
                                textAlign: "center",
                                backgroundColor: "rgba(255,255,255,0.03)",
                                borderRadius: 2,
                                px: 0.5,
                                py: 0.5,
                              }}
                            >
                              "{last_session.aiprofile_comments}"
                            </Typography>
                          </>
                        )}
                        <Box
                          mt={3}
                          display="flex"
                          flexWrap="wrap"
                          justifyContent="center"
                          gap={2}
                        >

                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePracticeAgain}
                            sx={{
                              borderRadius: 20,
                              px: 4,
                              minWidth: 140,
                              textTransform: "none",
                              height: 42,
                              fontWeight: 500,
                            }}
                          >
                            {t("last_session_button")}
                          </Button>

                          {last_session.aiprofile_comments && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleViewSession}
                              sx={{
                                borderRadius: 20,
                                px: 4,
                                minWidth: 140,
                                textTransform: "none",
                                height: 42,
                                fontWeight: 500,
                                borderColor: "#a855f7",
                                color: "#a855f7",
                                "&:hover": {
                                  borderColor: "#a855f7",
                                  backgroundColor: "rgba(168, 85, 247, 0.1)",
                                },
                              }}
                            >
                              {t("last_session_review")}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Card>
              </Grow>
            )}

            {/* CTA Sessions */}
            <Grow in timeout={1200}>
              <Box textAlign="center" mt={4}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/sessions")}
                  sx={{ color: "#fff", borderColor: "#fff" }}
                >
                  {t("view_all_sessions")}
                </Button>
              </Box>
            </Grow>

            {/* Métricas */}
            <Grow in timeout={1300}>
              <Box mt={4} backgroundColor="rgba(255, 255, 255, 0.05)" pt={1} pb={0} px={2} borderRadius={2}>
                <Accordion
                  defaultExpanded
                  expanded={metricsAccordionOpen}
                  onChange={handlemetricsAccordionToggle}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: "transparent",
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: "#fff", mt: "0px" }} />
                    }
                    sx={{
                      px: 0,
                      pb: 0,
                      mt: 1,
                      mb: 2,
                      color: "#fff",
                      alignItems: "flex-start",
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                        margin: 0,
                        "&.Mui-expanded": {
                          margin: 0,
                        },
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        // fontWeight="bold"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0, py: 0 }}
                      >
                        <AssessmentIcon fontSize="small" sx={{ color: "#a855f7" }} />
                        {t("individual_metrics")}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#aaa", mt: 0 , mb: 1 }}>
                        {t("metrics_summary_info")}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2, pt: 0, mt: 0 , mb: 2 }}>
                    <Box mt={0}>
                      {/* Objetivo: todo el ancho */}
                      <Card
                        sx={{
                          backgroundColor: "#1c1c1c",
                          minHeight: 60,
                          px: 2,
                          py: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t("metrics.objective")}{" "}
                          {metrics.objective != null && recent_progress?.objective_achievement != null && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.8rem",
                                color:
                                  recent_progress.objective_achievement > 0
                                    ? "#4CAF50"
                                    : recent_progress.objective_achievement < 0
                                    ? "#EF5350"
                                    : "#aaa",
                                ml: 1,
                              }}
                            >
                              {recent_progress.objective_achievement > 0
                                ? "▲"
                                : recent_progress.objective_achievement < 0
                                ? "▼"
                                : "●"}{" "}
                              {Math.abs(recent_progress.objective_achievement).toFixed(1)}
                            </Box>
                          )}
                        </Typography>
                        {metrics.objective != null ? (
                          <Rating
                            value={metrics.objective}
                            readOnly
                            precision={0.1}
                            sx={{ color: "#4CAF50" }}
                            icon={<CheckCircleIcon fontSize="inherit" />}
                            emptyIcon={<CheckCircleOutlineIcon fontSize="inherit" />}
                          />
                        ) : null}
                      </Card>

                      {/* Métricas restantes en layout responsive */}
                      <Box display="flex" flexWrap="wrap" gap={1} justifyContent="space-between">
                        {metricList
                          .filter((m) => m.key !== "objective")
                          .map((metric) => (
                            <Card
                              key={metric.key}
                              sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" },
                                backgroundColor: "#1c1c1c",
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                minHeight: 60,
                              }}
                            >
                              <Typography fontWeight="bold" mb={1}>
                                {metric.label}{" "}
                                {metric.progress != null && (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "0.8rem",
                                      color:
                                        metric.progress > 0
                                          ? "#4CAF50"
                                          : metric.progress < 0
                                          ? "#EF5350"
                                          : "#aaa",
                                      ml: 1,
                                    }}
                                  >
                                    {metric.progress > 0 ? "▲" : metric.progress < 0 ? "▼" : "●"}{" "}
                                    {Math.abs(metric.progress).toFixed(1)}
                                  </Box>
                                )}
                              </Typography>
                              <Rating
                                value={metric.value}
                                precision={0.1}
                                readOnly
                                size="small"
                                sx={{ color: "#FFD700" }}
                              />
                            </Card>
                          ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grow>

            {/* Logros */}
            <Grow in timeout={1400}>
              <Box backgroundColor="rgba(255, 255, 255, 0.05)" mt={3} mb={3} pt={1} pb={4} px={0.5} borderRadius={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  px={1}
                  mt={1}
                  mb={2}
                  sx={{ cursor: "pointer", position: "relative" }} // agrega esto!
                  onClick={() => navigate("/achievements")}
                >
                  <EmojiEvents
                    sx={{
                      fontSize: 24,
                      color: "#FFD700",
                      animation: "pulse 2.5s ease-in-out infinite",
                      transition: "transform 0.2s ease",
                      filter: "drop-shadow(0 0 6px rgba(255, 215, 0, 0.5))",
                      verticalAlign: "text-bottom",
                      mb: "6px",
                      "@keyframes pulse": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          filter: "drop-shadow(0 0 4px rgba(255, 215, 0, 0.3))",
                        },
                        "50%": {
                          transform: "scale(1.15)",
                          filter: "drop-shadow(0 0 8px rgba(255, 215, 0, 0.7))",
                        },
                      },
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t("achievements_title")}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      position: "absolute",
                      right: 18,
                      top: "44%",
                      transform: "translateY(-50%)",
                      fontSize: 24,
                      color: "#fff",
                    }}
                  >
                    ›
                  </Typography>
                </Box>

                {achievements.length === 0 ? (
                  <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
                    {t("no_achievements")}
                  </Typography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={{ xs: 0.5, sm: 2 }} justifyContent="center">
                    {achievements.map((a) => {
                      const MuiIcon = a.icon_mui && muiIconMap[a.icon_mui];
                      return (
                        <Box
                          key={a.id}
                          sx={{
                            width: "130px",
                            backgroundColor: "#1c1c1c",
                            borderRadius: 2,
                            textAlign: "center",
                            py: 2,
                            px: 0.5,
                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 4px 20px rgba(168, 85, 247, 0.3)",
                            },
                            boxShadow: {
                              xs: "0 0 12px rgba(168, 85, 247, 0.4)",
                              sm: "none",
                            },
                            animation: {
                              xs: "glow 2.5s ease-in-out infinite",
                              sm: "none",
                            },
                            "@keyframes glow": {
                              "0%, 100%": { boxShadow: "0 0 12px rgba(168, 85, 247, 0.2)" },
                              "50%": { boxShadow: "0 0 20px rgba(168, 85, 247, 0.7)" },
                            },
                          }}

                        >
                          {MuiIcon ? (
                            <MuiIcon sx={{ fontSize: 40, color: iconColorMap[a.icon_color] || iconColorMap.default }} />
                          ) : (
                            <Typography variant="h4" sx={{ color: a.icon_color || "#aaa" }}>
                              {a.icon || "🏆"}
                            </Typography>
                          )}
                          <Box sx={{ px: 1, pt: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {i18n.language?.startsWith("es") ? a.name_es : a.name_en}
                            </Typography>
                            <Typography variant="caption">
                              {i18n.language?.startsWith("es")  ? a.description_es : a.description_en}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

              </Box>
            </Grow>

            {/* NEW PITCH BUTTON */}
            <Grow in timeout={1500}>
              <Box
                sx={{
                  textAlign: "center",
                  mt: 4,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/pitch-start")}
                  sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 } }}
                >
                  <ReplayIcon sx={{ fontSize: 20 }} /> {t("new_session")}
                </Button>
              </Box>
            </Grow>
            </Box>
          </Fade>
        </Box>
      </AppBackground>
    </>
  );
};

export default Dashboard;
